import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ApiURl from '../Paths/Api';

const Main = () => {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [formData, setFormData] = useState({
    phone: '',
    password: '',
    companyName: '',
    personName: '',
    mail: '',
    gstNumber: '',
    otp: '',
    newPassword: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [otpDeliveryStatus, setOtpDeliveryStatus] = useState(null);

  // Helper function to log with timestamp
  const logWithTimestamp = (message, data) => {
    const timestamp = new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
    console.log(`[${timestamp}] ${message}`, data);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    logWithTimestamp('Login Form Submission Started', { phone: formData.phone });
    if (!formData.phone || !formData.password) {
      setError('Please fill out all required fields');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${ApiURl}/login.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: formData.phone,
          password: formData.password,
        }),
      });

      const data = await response.json();
      logWithTimestamp('Login API Response', { data });

      if (data.success) {
        if (data.user.role === 'admin') {
          localStorage.setItem('userId', data.user.id);
          localStorage.setItem('role', data.user.role);
          localStorage.setItem('full_name', data.user.full_name);
          localStorage.setItem('phone', data.user.phone);
          localStorage.setItem('company_name', data.user.company_name || '');
          localStorage.setItem('token', data.user.token || '');
          navigate('/dashboard');
        } else {
          setError('Only users can log in');
        }
      } else {
        setError(data.message || 'Invalid credentials');
      }
    } catch (err) {
      setError('Error occurred, please try again');
      logWithTimestamp('Login API Error', { error: err.message });
    } finally {
      setIsSubmitting(false);
    }
  };

const handleSendOtp = async (e) => {
  e.preventDefault();

  logWithTimestamp('Send OTP Form Submission Started', {
    companyName: formData.companyName,
    personName: formData.personName,
    email: formData.mail,
    gstNumber: formData.gstNumber,
    phone: formData.phone || '(not provided)',
  });

  // Required fields (phone is NOT required)
  const requiredFields = ['companyName', 'personName', 'mail', 'gstNumber'];
  for (const field of requiredFields) {
    if (!formData[field]?.trim()) {
      setError('Please fill out all required fields');
      return;
    }
  }

  // Email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(formData.mail.trim())) {
    setError('Please enter a valid email address');
    return;
  }

  // Phone validation — only if user typed something
  if (formData.phone?.trim()) {
    const cleanPhone = formData.phone.replace(/\D/g, '');
    if (cleanPhone.length !== 10) {
      setError('Phone number must be exactly 10 digits');
      return;
    }
  }

  setIsSubmitting(true);
  setError(null);                    // Clear previous errors
  setOtpDeliveryStatus(null);

  try {
    const payload = {
      email: formData.mail.trim(),
    };

    // Send phone only if provided (optional for future use)
    if (formData.phone?.trim()) {
      payload.phone = formData.phone.replace(/\D/g, '');
    }

    const response = await fetch(`${ApiURl}/send_otp.php`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    const data = await response.json();
    logWithTimestamp('OTP API Response', data);

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to send OTP');
    }

    // SUCCESS
    setIsOtpSent(true);
    setOtpDeliveryStatus({ email: 'success' });

    // Show success message using the same setError (green color in your UI)
    // Most projects style positive messages differently when error is null + success flag
    setError('OTP sent successfully! Check your email (including spam).');

    // Optional: if you have a separate success state, use it:
    // setSuccess && setSuccess('OTP sent to your email!');

  } catch (err) {
    logWithTimestamp('OTP Send Failed', err.message);
    setError(err.message || 'Failed to send OTP. Please try again.');
    setOtpDeliveryStatus({ email: 'failed' });
  } finally {
    setIsSubmitting(false);
  }
};

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    logWithTimestamp('Verify OTP Form Submission Started', { otp: formData.otp });
    if (!formData.otp) {
      setError('Please enter the OTP');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${ApiURl}/verify_otp.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: formData.phone,
          otp: formData.otp,
        }),
      });

      const data = await response.json();
      logWithTimestamp('Verify OTP API Response', { data });

      if (data.success) {
        setIsOtpVerified(true);
        setError('OTP verified successfully! Please set your password.');
      } else {
        setError(data.message || 'Invalid OTP');
      }
    } catch (err) {
      setError('Error occurred, please try again');
      logWithTimestamp('Verify OTP API Error', { error: err.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    logWithTimestamp('Signup Form Submission Started', { phone: formData.phone });
    if (!formData.newPassword) {
      setError('Please enter a password');
      return;
    }

    if (formData.newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${ApiURl}/signup.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyName: formData.companyName,
          personName: formData.personName,
          mail: formData.mail,
          gstNumber: formData.gstNumber,
          phone: formData.phone,
          password: formData.newPassword,
        }),
      });

      const data = await response.json();
      logWithTimestamp('Signup API Response', { data });

      if (data.success) {
        localStorage.setItem('userId', data.user.id);
        localStorage.setItem('role', data.user.role);
        localStorage.setItem('full_name', data.user.full_name);
        localStorage.setItem('company_name', data.user.company_name);
        localStorage.setItem('email', data.user.email);
        localStorage.setItem('phone', data.user.phone);

        // Show success message and redirect to login
        setError('Signup successful! Please login with your credentials.');
        setTimeout(() => {
          setIsLogin(true);
          setFormData({
            phone: '',
            password: '',
            companyName: '',
            personName: '',
            mail: '',
            gstNumber: '',
            otp: '',
            newPassword: '',
          });
          setError(null);
          setIsOtpSent(false);
          setIsOtpVerified(false);
          setOtpDeliveryStatus(null);
        }, 2000);
      } else {
        setError(data.message || 'Signup failed');
      }
    } catch (err) {
      setError('Error occurred, please try again');
      logWithTimestamp('Signup API Error', { error: err.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const switchToSignup = () => {
    setIsLogin(false);
    setFormData({
      phone: '',
      password: '',
      companyName: '',
      personName: '',
      mail: '',
      gstNumber: '',
      otp: '',
      newPassword: '',
    });
    setError(null);
    setIsOtpSent(false);
    setIsOtpVerified(false);
    setOtpDeliveryStatus(null);
  };

  const switchToLogin = () => {
    setIsLogin(true);
    setFormData({
      phone: '',
      password: '',
      companyName: '',
      personName: '',
      mail: '',
      gstNumber: '',
      otp: '',
      newPassword: '',
    });
    setError(null);
    setIsOtpSent(false);
    setIsOtpVerified(false);
    setOtpDeliveryStatus(null);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const formFields = [
    { name: 'companyName', label: 'Company Name', type: 'text', required: true },
    { name: 'personName', label: 'Person Name', type: 'text', required: true },
    { name: 'mail', label: 'Email', type: 'email', required: true },
    { name: 'gstNumber', label: 'GST Number', type: 'text', required: true },
    { name: 'phone', label: 'Phone Number', type: 'tel', required: true },
  ];

  return (
    <div className="relative min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 font-sans flex flex-col">
      {/* Embedded CSS for animations */}
      <style jsx>{`
        .animate-fade-in {
          animation: fade-in 0.6s ease-out;
        }
        .animate-slide-in {
          animation: slide-in 0.5s ease-out;
        }
        .animate-pop-in {
          animation: pop-in 0.4s ease-out;
        }
        .animate-grow {
          animation: grow 0.6s ease-out;
        }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(15px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slide-in {
          from { opacity: 0; transform: translateX(-15px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes pop-in {
          from { transform: scale(0.9); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        @keyframes grow {
          from { transform: scale(0.9); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
      `}</style>

      {/* Navbar */}
      <nav className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white p-4 shadow-lg fixed w-full z-20">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center">
            <span className="text-xl font-bold tracking-tight">Event Manager</span>
          </div>
          <button
            onClick={handleBack}
            className="px-4 py-2 bg-teal-600 text-white rounded-lg text-base font-medium hover:bg-teal-700 transition-all duration-300 transform hover:scale-105 flex items-center"
            aria-label="Go back"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-grow flex items-center justify-center p-6 lg:p-8 pt-24">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-8 border border-gray-200 animate-pop-in">
          {isLogin ? (
            <>
              <div className="text-center mb-6">
                <h2 className="text-xl font-semibold text-teal-600">User Login</h2>
                <p className="text-gray-600 mt-1 text-base sm:text-sm">Access your event management dashboard</p>
              </div>

              {error && (
                <div className={`mb-4 p-3 rounded-lg border text-sm animate-slide-in ${error.includes('success')
                  ? 'bg-green-50 text-green-600 border-green-100'
                  : 'bg-red-50 text-red-600 border-red-100'
                  }`}>
                  {error}
                </div>
              )}

              <form onSubmit={handleLoginSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Enter 10-digit phone number"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 transition-all duration-300 hover:border-teal-400"
                    required
                    pattern="[0-9]{10}"
                    maxLength="10"
                    aria-required="true"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Password <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Enter password"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 transition-all duration-300 hover:border-teal-400"
                    required
                    aria-required="true"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full py-2 px-4 bg-teal-500 text-white rounded-lg text-base font-medium transition-all duration-300 transform hover:scale-105 hover:bg-teal-600 flex items-center justify-center ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
                    }`}
                  aria-label="Login"
                >
                  {isSubmitting ? (
                    <>
                      <svg
                        className="animate-spin h-5 w-5 mr-2 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Logging in...
                    </>
                  ) : (
                    'Login'
                  )}
                </button>
              </form>

              <div className="mt-4 text-center text-sm">
                <p>
                  Don't have an account?{' '}
                  <button
                    onClick={switchToSignup}
                    className="text-teal-600 hover:text-teal-700 font-medium transition-colors duration-300"
                  >
                    Sign Up
                  </button>
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="text-center mb-6">
                <h2 className="text-xl font-semibold text-teal-600">
                  {isOtpVerified ? 'Set Password' : isOtpSent ? 'Verify OTP' : 'Sign Up'}
                </h2>
                <p className="text-gray-600 mt-1 text-base sm:text-sm">
                  {isOtpVerified
                    ? 'Create a secure password'
                    : isOtpSent
                      ? 'Enter the OTP sent to your phone/email'
                      : 'Register to start managing events'}
                </p>
              </div>

              {error && (
                <div className={`mb-4 p-3 rounded-lg border text-sm animate-slide-in ${error.includes('success') || error.includes('sent successfully')
                  ? 'bg-green-50 text-green-600 border-green-100'
                  : 'bg-red-50 text-red-600 border-red-100'
                  }`}>
                  {error}
                </div>
              )}

              {!isOtpSent ? (
                <form onSubmit={handleSendOtp} className="space-y-4">
                  {formFields.map((field) => (
                    <div key={field.name}>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {field.label} <span className="text-red-500">*</span>
                      </label>
                      <input
                        type={field.type}
                        name={field.name}
                        value={formData[field.name]}
                        onChange={handleChange}
                        placeholder={`Enter ${field.label.toLowerCase()}`}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 transition-all duration-300 hover:border-teal-400"
                        required
                        aria-required="true"
                        {...(field.name === 'phone' && {
                          pattern: "[0-9]{10}",
                          maxLength: "10"
                        })}
                      />
                    </div>
                  ))}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full py-2 px-4 bg-teal-500 text-white rounded-lg text-base font-medium transition-all duration-300 transform hover:scale-105 hover:bg-teal-600 flex items-center justify-center ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
                      }`}
                    aria-label="Send OTP"
                  >
                    {isSubmitting ? (
                      <>
                        <svg
                          className="animate-spin h-5 w-5 mr-2 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Sending OTP...
                      </>
                    ) : (
                      'Send OTP'
                    )}
                  </button>
                </form>
              ) : !isOtpVerified ? (
                <form onSubmit={handleVerifyOtp} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      OTP <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="otp"
                      value={formData.otp}
                      onChange={handleChange}
                      placeholder="Enter 6-digit OTP"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 transition-all duration-300 hover:border-teal-400"
                      required
                      pattern="[0-9]{6}"
                      maxLength="6"
                      aria-required="true"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full py-2 px-4 bg-teal-500 text-white rounded-lg text-base font-medium transition-all duration-300 transform hover:scale-105 hover:bg-teal-600 flex items-center justify-center ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
                      }`}
                    aria-label="Verify OTP"
                  >
                    {isSubmitting ? (
                      <>
                        <svg
                          className="animate-spin h-5 w-5 mr-2 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Verifying OTP...
                      </>
                    ) : (
                      'Verify OTP'
                    )}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleSignupSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Create Password <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      name="newPassword"
                      value={formData.newPassword}
                      onChange={handleChange}
                      placeholder="Enter at least 6 characters"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 transition-all duration-300 hover:border-teal-400"
                      required
                      minLength="6"
                      aria-required="true"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full py-2 px-4 bg-teal-500 text-white rounded-lg text-base font-medium transition-all duration-300 transform hover:scale-105 hover:bg-teal-600 flex items-center justify-center ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''
                      }`}
                    aria-label="Complete Signup"
                  >
                    {isSubmitting ? (
                      <>
                        <svg
                          className="animate-spin h-5 w-5 mr-2 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Signing up...
                      </>
                    ) : (
                      'Complete Signup'
                    )}
                  </button>
                </form>
              )}

              <div className="mt-4 text-center text-sm">
                <p>
                  Already have an account?{' '}
                  <button
                    onClick={switchToLogin}
                    className="text-teal-600 hover:text-teal-700 font-medium transition-colors duration-300"
                  >
                    Login
                  </button>
                </p>
              </div>
            </>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="relative w-full py-4 text-center text-gray-600 text-sm z-10">
        <a href="https://algo2botsinfotech.com/home/" className="hover:text-teal-600 transition-all duration-300">
          Powered by @ Algo2bots Infotech • {new Date().getFullYear()}
        </a>
      </footer>
    </div>
  );
};

export default Main;